import { ACTION_TYPES, PHASES } from '../constants';
import { takeCard, declineCard, canDecline, canTake, mustTake, activePlayer } from './turnActions';
import { nextRound } from './roundActions';
import { scoreboard, matchStandings } from '../scoring';

export { ACTION_TYPES, PHASES };
export { canDecline, canTake, mustTake, activePlayer };

/**
 * The single entry point for every state change: dispatch(state, action).
 *
 * Same shape as the space-base and thunderstone engines - one reducer, plain
 * JSON state in and out, no framework imports anywhere under src/engine
 * (enforced by eslint). The UI never mutates state; it dispatches.
 *
 * An unknown or illegal action returns the state unchanged. Legality is the
 * caller's to check first (canTake / canDecline) - this is the backstop, not
 * the validation layer.
 */
export const dispatch = (state, action) => {
  if (!state) return state;
  // Between rounds of a match, dealing the next one is the only legal move.
  if (state.meta.phase === PHASES.ROUND_OVER) {
    return action?.type === ACTION_TYPES.NEXT_ROUND ? nextRound(state) : state;
  }
  if (state.meta.phase !== PHASES.PLAYING) return state;

  switch (action?.type) {
    case ACTION_TYPES.TAKE_CARD:
      return takeCard(state);
    case ACTION_TYPES.DECLINE_CARD:
      return declineCard(state);
    default:
      return state;
  }
};

export const takeCardAction = () => ({ type: ACTION_TYPES.TAKE_CARD });
export const declineCardAction = () => ({ type: ACTION_TYPES.DECLINE_CARD });
export const nextRoundAction = () => ({ type: ACTION_TYPES.NEXT_ROUND });

/**
 * Everything the UI needs about how a finished game came out. Null while the
 * game is still running, so a component can render on it directly.
 */
export const finalScores = (state) => {
  if (!state || state.meta.phase !== PHASES.FINISHED) return null;
  return scoreboard(state.players);
};

/** True once the current round's last card has gone, match over or not. */
export const isRoundOver = (state) => Boolean(state)
  && (state.meta.phase === PHASES.ROUND_OVER || state.meta.phase === PHASES.FINISHED);

/**
 * The board for the round just played - one round is one rulebook game.
 * Null while the round is still running. Lifetime stats record this, one
 * entry per round, so a best score is always a single game's score.
 */
export const roundScores = (state) => (isRoundOver(state) ? scoreboard(state.players) : null);

/** Where a multi-round match stands: { round, rounds, over }. Rounds default to 1. */
export const matchInfo = (state) => ({
  round: state?.meta?.round ?? 1,
  rounds: state?.meta?.rounds ?? 1,
  over: state?.meta?.phase === PHASES.FINISHED,
});

/**
 * What the score sheet shows. A one-round game is the rulebook scoreboard,
 * untouched. In a longer match, players are ranked by running total and each
 * row also carries this round's own result.
 */
export const standings = (state) => {
  if (!isRoundOver(state)) return null;
  if ((state.meta.rounds ?? 1) <= 1) return scoreboard(state.players);
  return matchStandings(state.players, state.roundResults || []);
};

/**
 * How many cards are left to be dealt, including the one currently face up.
 * The players never learn WHICH nine are missing, but the count remaining is
 * public information - it's a visible stack on the table.
 */
export const cardsRemaining = (state) => state.deck.length + (state.currentCard === null ? 0 : 1);
